import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

// ==================== TOKEN STORAGE ====================
const ACCESS_TOKEN_KEY = "festa_access_token";
const REFRESH_TOKEN_KEY = "festa_refresh_token";

export const tokenStorage = {
  getAccess: () => {
    try { return localStorage.getItem(ACCESS_TOKEN_KEY); } catch { return null; }
  },
  getRefresh: () => {
    try { return localStorage.getItem(REFRESH_TOKEN_KEY); } catch { return null; }
  },
  set: ({ access_token, refresh_token } = {}) => {
    try {
      if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
      if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    } catch {
      /* ignore storage errors */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

const api = axios.create({
  baseURL: API,
  // Cookies kept for backward compatibility with same-origin sessions, but
  // primary auth is now via Authorization: Bearer <token> header so the app
  // works on custom domains where the proxy strips/rewrites cross-origin
  // cookie headers.
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ==================== REQUEST INTERCEPTOR ====================
api.interceptors.request.use((config) => {
  const token = tokenStorage.getAccess();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // TV pairing: if a tv_token is stored in localStorage (set by TVPairing
  // page after a successful pair), and there's no normal Bearer auth,
  // attach it via the TVToken scheme so the backend can authorize
  // event-scoped reads (e.g. /spotify/token) on the TV.
  if (!token) {
    try {
      // Find any festa_tv_token_<event_id> key
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("festa_tv_token_")) {
          const tv = localStorage.getItem(key);
          if (tv) {
            config.headers = config.headers || {};
            config.headers.Authorization = `TVToken ${tv}`;
            break;
          }
        }
      }
    } catch (_) { /* ignore */ }
  }
  return config;
});

// ==================== RESPONSE INTERCEPTOR (auto-refresh on 401) ====================
let refreshInFlight = null;

async function performRefresh() {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) throw new Error("No refresh token");
  // Plain axios call (no interceptors) to avoid loops.
  const resp = await axios.post(
    `${API}/auth/refresh`,
    { refresh_token: refresh },
    {
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refresh}`,
      },
    }
  );
  const newAccess = resp.data?.access_token;
  if (!newAccess) throw new Error("Refresh failed");
  tokenStorage.set({ access_token: newAccess });
  return newAccess;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;
    const status = error?.response?.status;
    const url = original?.url || "";
    // Only attempt refresh on 401 from authenticated endpoints, once per request.
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh");
    if (status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      try {
        if (!refreshInFlight) refreshInFlight = performRefresh().finally(() => { refreshInFlight = null; });
        const newAccess = await refreshInFlight;
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch (e) {
        tokenStorage.clear();
      }
    }
    return Promise.reject(error);
  }
);

// Error formatter
export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// ==================== AUTH ====================
export const authApi = {
  register: async (data) => {
    const res = await api.post("/auth/register", data);
    tokenStorage.set(res.data || {});
    return res;
  },
  login: async (data) => {
    const res = await api.post("/auth/login", data);
    tokenStorage.set(res.data || {});
    return res;
  },
  logout: async () => {
    try {
      return await api.post("/auth/logout");
    } finally {
      tokenStorage.clear();
    }
  },
  me: () => api.get("/auth/me"),
  changePassword: (currentPassword, newPassword) =>
    api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword }),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) =>
    api.post("/auth/reset-password", { token, new_password: newPassword }),
  refresh: async () => {
    const newAccess = await performRefresh();
    return { data: { access_token: newAccess } };
  },
};

// Events
export const eventsApi = {
  create: (data) => api.post("/events", data),
  list: () => api.get("/events"),
  get: (eventId) => api.get(`/events/${eventId}`),
  update: (eventId, data) => api.put(`/events/${eventId}`, data),
  delete: (eventId) => api.delete(`/events/${eventId}`),
  stats: (eventId) => api.get(`/events/${eventId}/stats`),
  end: (eventId) => api.post(`/events/${eventId}/end`),
  reactivate: (eventId) => api.post(`/events/${eventId}/reactivate`),
  updateSettings: (eventId, data) => api.put(`/events/${eventId}/settings`, data),
  checkout: (eventId, originUrl) => api.post(`/events/${eventId}/checkout`, { event_id: eventId, origin_url: originUrl }),
  pixCheckout: (eventId, originUrl) => api.post(`/events/${eventId}/pix-checkout`, { event_id: eventId, origin_url: originUrl }),
  paymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  checkUnlock: (eventId, deviceId) => api.get(`/events/${eventId}/check-unlock?device_id=${deviceId}`),
  generateVideo: (eventId) => api.post(`/events/${eventId}/generate-video`),
  videoStatus: (eventId) => api.get(`/events/${eventId}/video-status`),
  videoCheckout: (eventId, originUrl, musicId) => api.post(`/events/${eventId}/video-checkout`, { event_id: eventId, origin_url: originUrl, music_id: musicId }),
  pixVideoCheckout: (eventId, originUrl, musicId) => api.post(`/events/${eventId}/pix-video-checkout`, { event_id: eventId, origin_url: originUrl, music_id: musicId }),
  generateReels: (eventId, musicId) => api.post(`/events/${eventId}/generate-reels`, { music_id: musicId }),
  uploadMusic: (eventId, formData) => api.post(`/events/${eventId}/upload-music`, formData, { headers: { "Content-Type": "multipart/form-data" } }),
  getMusicLibrary: () => api.get("/music/library"),
};

// Guests
export const guestApi = {
  join: (eventId, data) => api.post(`/events/${eventId}/join`, data),
};

// Media
export const mediaApi = {
  upload: (eventId, formData, onUploadProgress) =>
    api.post(`/events/${eventId}/upload`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    }),
  feed: (eventId, after = null) => {
    const params = after ? `?after=${encodeURIComponent(after)}` : "";
    return api.get(`/events/${eventId}/feed${params}`);
  },
  like: (mediaId, guestId) => {
    const formData = new FormData();
    formData.append("guest_id", guestId || "");
    return api.post(`/media/${mediaId}/like`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  checkLiked: (mediaId, userId) => api.get(`/media/${mediaId}/liked?user_id=${userId}`),
  top: (eventId) => api.get(`/events/${eventId}/top`),
  // Host-only: hide/show a specific media on the TV slideshow.
  setShowOnTv: (mediaId, show) =>
    api.put(`/media/${mediaId}/show-on-tv`, { show_on_tv: show }),
};

// Albums
export const albumsApi = {
  list: (eventId) => api.get(`/events/${eventId}/albums`),
  media: (eventId, albumName) => api.get(`/events/${eventId}/albums/${encodeURIComponent(albumName)}/media`),
  downloadAlbum: (eventId, albumName) => `${API}/events/${eventId}/albums/${encodeURIComponent(albumName)}/download`,
  downloadAll: (eventId) => `${API}/events/${eventId}/download`,
};

// Slideshow
export const slideshowApi = {
  get: (eventId, limit = 200) => api.get(`/events/${eventId}/slideshow?limit=${limit}`),
};

// TV Pairing (Netflix-style device pairing)
export const tvApi = {
  requestCode: (deviceLabel = "TV") => api.post("/tv/code", { device_label: deviceLabel }),
  checkStatus: (code) => api.get(`/tv/status/${encodeURIComponent(code)}`),
  pair: (code, eventId) => api.post("/tv/pair", { code, event_id: eventId }),
  verifyToken: (tvToken) => api.get("/tv/verify-token", { headers: { Authorization: `TVToken ${tvToken}` } }),
  listDevices: (eventId) => api.get(`/events/${eventId}/tv-devices`),
  revokeDevice: (eventId, tokenSuffix) => api.delete(`/events/${eventId}/tv-devices/${tokenSuffix}`),
};


// Per-event host configuration (content rules + payments scoped to event)
export const eventConfigApi = {
  getRules: (eventId) => api.get(`/events/${eventId}/content-rules`),
  updateRules: (eventId, rules) => api.put(`/events/${eventId}/content-rules`, rules),
  getPayments: (eventId) => api.get(`/events/${eventId}/payments`),
  // Live TV (YouTube live broadcast embedded in TV mode)
  getLiveTv: (eventId) => api.get(`/events/${eventId}/live-tv`),
  updateLiveTv: (eventId, payload) => api.put(`/events/${eventId}/live-tv`, payload),
};

// TV background music playlist (per event)
export const musicApi = {
  list: (eventId) => api.get(`/events/${eventId}/music`),
  publicList: (eventId) => api.get(`/events/${eventId}/music/public`),
  upload: (eventId, formData, onUploadProgress) =>
    api.post(`/events/${eventId}/music`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    }),
  remove: (eventId, trackId) => api.delete(`/events/${eventId}/music/${trackId}`),
};

// Spotify per-event integration. The host can connect their Spotify account
// to a specific event and pick a playlist that plays on the TV mode via the
// Web Playback SDK (Premium account required for SDK playback).
export const spotifyApi = {
  // Returns { auth_url } — frontend should redirect/window.open the URL.
  // `origin` (window.location.origin) tells the backend where to send the
  // user back after Spotify approves access (festaia.com.br vs preview).
  startLogin: (eventId, origin) =>
    api.get(`/spotify/login?event_id=${eventId}${origin ? `&origin=${encodeURIComponent(origin)}` : ""}`),
  status: (eventId) => api.get(`/spotify/status?event_id=${eventId}`),
  playlists: (eventId) => api.get(`/spotify/playlists?event_id=${eventId}`),
  selectPlaylist: (eventId, playlist) =>
    api.post(`/spotify/select-playlist`, {
      event_id: eventId,
      playlist_uri: playlist.uri,
      playlist_name: playlist.name,
      playlist_image: playlist.image || null,
    }),
  disconnect: (eventId) => api.delete(`/spotify/disconnect?event_id=${eventId}`),
  // Returns { access_token } — used to feed Spotify Web Playback SDK.
  token: (eventId) => api.get(`/spotify/token?event_id=${eventId}`),
};

// File URL helper
export const getFileUrl = (storagePath) => `${API}/files/${storagePath}`;

// Admin panel — list/block/unblock host accounts.
// Routes are NOT under /api/api: adminApi endpoints are mounted directly on
// `app` at /api/admin/* for consistency with /api/admin/wipe-data.
export const adminApi = {
  listUsers: () => axios.get(`${API}/admin/users`, {
    withCredentials: true,
    headers: { Authorization: `Bearer ${tokenStorage.getAccess() || ""}` },
  }),
  blockUser: (userId, reason) => axios.post(`${API}/admin/users/${userId}/block`, { reason }, {
    withCredentials: true,
    headers: { Authorization: `Bearer ${tokenStorage.getAccess() || ""}`, "Content-Type": "application/json" },
  }),
  unblockUser: (userId) => axios.post(`${API}/admin/users/${userId}/unblock`, {}, {
    withCredentials: true,
    headers: { Authorization: `Bearer ${tokenStorage.getAccess() || ""}` },
  }),
  // TV APK hosting: upload once, the public URL `/api/tv-apk` will then serve it.
  tvApkInfo: () => axios.get(`${API}/tv-apk/info`),
  uploadTvApk: (file, onUploadProgress) => {
    const form = new FormData();
    form.append("file", file);
    return axios.post(`${API}/admin/tv-apk`, form, {
      withCredentials: true,
      headers: { Authorization: `Bearer ${tokenStorage.getAccess() || ""}` },
      onUploadProgress,
      // APKs can be large; allow up to 10 min for slow connections.
      timeout: 600_000,
    });
  },
};

// Date format helper (YYYY-MM-DD → dd/mm/aaaa)
export function formatDate(dateStr) {
  if (!dateStr) return "";
  const parts = dateStr.split("T")[0].split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
}

// WebSocket URL helper - converts REACT_APP_BACKEND_URL (http/https) into the
// ws/wss URL for the realtime event feed endpoint.
export function getEventFeedWsUrl(eventId) {
  const base = API_URL || "";
  // Replace http(s) -> ws(s) while keeping host intact.
  const wsBase = base.replace(/^http/i, "ws");
  return `${wsBase}/api/ws/events/${eventId}`;
}

export default api;
